/**
 * The farm log, reduced to what the two Farm surfaces draw.
 *
 * `FarmLog.tsx` used to do this inline and grew eleven screens and 9,004px doing it, most of that
 * explanation. The screen is now one roster card and one flat list, so the shaping belongs here:
 * the component's job is to arrange what this module decides, and nothing here knows about pixels.
 *
 * The two eras the log spans still have to reconcile, and they do it the way they always did —
 * one order column, because both eras share the same underlying fact (whose turn it was, and
 * when). What differs is only what happened on that turn:
 *
 *   2013-2016, 2018 — a supplemental SNAKE round. Your slot in the order was your turn.
 *   2017, 2019-2025 — an AUCTION. You nominate in serpentine order; the money decides.
 */

import log from '../data/farm-log.json';
import { schoolOf } from './farmSchools.js';

export interface Pick {
  farmYear: number; farmRound: number; slot: number | null; orderKey: number | null;
  orderKnown: boolean; orderSource: 'sheet' | 'draft-archive' | 'none';
  format: 'snake' | 'auction';
  bid: number | null; nominator: string; gm: string; via: string;
  outcome: 'won' | 'passed';
  player: string; pos: string; school: string; college: string;
  status: 'nfl' | 'college' | 'ended' | 'unknown' | 'passed';
  statusNote: string; statusSource: string;
  nflRaw: string; nflTeam: string;
  nflYear: number | null; nflRound: number | null; nflPick: number | null;
  nflOverall: number | null; nflUdfa: boolean;
  lgYear: number | null; lgRound: number | null; lgPick: number | null; lgOverall: number | null;
  lgNote: string;
  active: boolean;
}

export const PICKS = log as unknown as Pick[];

/** 2022 "Farm System Auction Declarations And Acquisitions" §1. */
export const FARM_CAP = 3;

/* -- the outcome chip ------------------------------------------------------------------------ */

export type OutcomeTone = 'drafted' | 'incollege' | 'udfa';

export interface OutcomeView {
  tone: OutcomeTone;
  /** Phone label — short, because the row has one line for it. */
  short: string;
  /** Desktop label — the whole fact, because the column is 132px wide and can hold it. */
  long: string;
  /** What the chip says when it is tapped. */
  detail: string;
  aria: string;
}

/**
 * The most recent NFL draft the log knows about.
 *
 * The phone chip names the CLUB for that year and the YEAR for every year before it, which is
 * what the design draws: for the newest class "which team took him" is the news, and for a 2019
 * pick the year is what orients you. Derived from the data rather than written as a constant, so
 * next August it moves on its own instead of quietly freezing on 2026.
 */
export const LATEST_NFL_YEAR = PICKS.reduce((n, p) => Math.max(n, p.nflYear ?? 0), 0);

function outcomeOf(p: Pick): OutcomeView | null {
  if (p.outcome === 'passed') return null;

  if (p.status === 'college') {
    const school = schoolOf(p) ?? 'college';
    return {
      tone: 'incollege', short: 'In college', long: 'In college',
      detail: p.statusNote || `Still in college at ${school}.`,
      aria: `Still in college at ${school}`,
    };
  }

  if (p.status === 'ended') {
    /*
     * Written off. There is no fourth tone in the design, and inventing one would break the "three
     * chip classes" rule — so a rights-ended player takes the graphite outcome, which is the tone
     * for "nothing happened in the NFL", and the whole story goes in the callout where it fits.
     */
    return {
      tone: 'incollege', short: 'Rights ended', long: 'Rights ended',
      detail: p.statusNote || 'Rights written off; no longer occupies a farm slot.',
      aria: 'Farm rights ended',
    };
  }

  const year = p.nflYear;
  if (p.nflUdfa) {
    return {
      tone: 'udfa',
      short: year ? `UDFA ${year}` : 'UDFA',
      long: year ? `${year} · UDFA` : 'UDFA',
      detail: [year ? `Undrafted in ${year}` : 'Undrafted', p.lgNote].filter(Boolean).join('; ') + '.',
      aria: year ? `Undrafted in ${year}` : 'Undrafted',
    };
  }

  if (year && p.nflRound) {
    const club = p.nflTeam;
    const tail = year === LATEST_NFL_YEAR && club ? club : String(year);
    const where = p.nflOverall ? `${p.nflOverall} overall`
      : p.nflPick ? `pick ${p.nflPick}` : '';
    return {
      tone: 'drafted',
      short: `Rd ${p.nflRound} · ${tail}`,
      long: [year, `Rd ${p.nflRound}`, p.nflOverall ? String(p.nflOverall) : '', club]
        .filter(Boolean).join(' · '),
      detail: [`${year} · Rd ${p.nflRound}`, where, club].filter(Boolean).join(' · '),
      aria: `${year} NFL draft, round ${p.nflRound}${where ? `, ${where}` : ''}` +
        (club ? `, ${club}` : ''),
    };
  }

  return {
    tone: 'incollege', short: 'No outcome', long: 'No outcome recorded',
    detail: 'The sheet recorded no NFL outcome for this player.',
    aria: 'No outcome recorded',
  };
}

/** When the league actually spent a fantasy pick on him, once he graduated. */
function leagueDraft(p: Pick): string {
  if (p.lgYear && p.lgRound) {
    return `${p.lgYear} · ${p.lgRound}.${p.lgPick}` + (p.lgOverall ? ` (${p.lgOverall})` : '');
  }
  if (p.lgNote) return p.lgNote;
  if (p.lgYear) return String(p.lgYear);
  return '';
}

/* -- one turn -------------------------------------------------------------------------------- */

export interface Turn {
  key: string;
  season: number;
  /** "1.03" — round-major, so a two-round year reads in the order it happened. */
  order: string;
  orderKey: number;
  passed: boolean;
  /** Whose turn it was. On a passed turn this is the whole row. */
  nominator: string;
  /** Who ended up with him. In an auction that is often not the nominator. */
  wonBy: string;
  player: string;
  pos: string;
  school: string | null;
  bid: number | null;
  outcome: OutcomeView | null;
  leagueDraft: string;
  status: Pick['status'];
}

const title = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1).toLowerCase() : s);

export function toTurn(p: Pick, i: number): Turn {
  return {
    key: `${p.farmYear}-${p.farmRound}-${p.slot ?? i}-${p.player || 'pass'}`,
    season: p.farmYear,
    order: p.slot === null ? '' : `${p.farmRound}.${String(p.slot).padStart(2, '0')}`,
    orderKey: p.orderKey ?? 9999,
    passed: p.outcome === 'passed',
    nominator: title(p.nominator),
    wonBy: title(p.gm),
    player: p.player,
    pos: p.pos,
    school: schoolOf(p),
    bid: p.bid,
    outcome: outcomeOf(p),
    leagueDraft: leagueDraft(p),
    status: p.status,
  };
}

export const TURNS: Turn[] = PICKS.map(toTurn);

/* -- the four stat cards --------------------------------------------------------------------- */

export interface FarmStats {
  acquired: number;
  reachedNfl: number;
  passed: number;
  inCollege: number;
}

/** Four, not five, and no sixth "insight" tile. */
export function farmStats(picks: readonly Pick[] = PICKS): FarmStats {
  return {
    acquired: picks.filter((p) => p.outcome === 'won').length,
    reachedNfl: picks.filter((p) => p.status === 'nfl').length,
    passed: picks.filter((p) => p.outcome === 'passed').length,
    inCollege: picks.filter((p) => p.status === 'college').length,
  };
}

/* -- season bands ---------------------------------------------------------------------------- */

export interface Band {
  season: number;
  /** "Auction · two rounds" — the phone's version, which has no room for the tally. */
  short: string;
  /** The desktop's, which adds either the tally or the provenance of the order. */
  long: string;
}

const ROUND_WORD = ['no', 'one', 'two', 'three', 'four'];

export function bandOf(season: number, rows: readonly Turn[], picks: readonly Pick[]): Band {
  const mine = picks.filter((p) => p.farmYear === season);
  const rounds = new Set(mine.map((p) => p.farmRound)).size;
  const method = mine[0]?.format === 'auction' ? 'Auction' : 'Supplemental round';
  const word = ROUND_WORD[rounds] ?? String(rounds);
  const short = `${method} · ${word} round${rounds === 1 ? '' : 's'}`;
  /*
   * The desktop band earns its extra words two different ways, and which one it gets is a fact
   * about the record rather than a style choice: where the sheet recorded the order we can tally
   * the season honestly, and where it did not, the order came out of the draft archives and the
   * band says so instead of implying a precision the row does not have.
   */
  const derived = mine.some((p) => p.orderSource === 'draft-archive');
  const sold = rows.filter((r) => !r.passed).length;
  const passed = rows.filter((r) => r.passed).length;
  const tail = derived
    ? 'slots derived from the draft archives'
    : `${sold} sold, ${passed} passed`;
  return { season, short, long: `${short} · ${tail}` };
}

/* -- the flat list ---------------------------------------------------------------------------- */

export type Item =
  | { kind: 'band'; key: string; band: Band }
  | { kind: 'turn'; key: string; turn: Turn };

export interface Filters {
  /** Matches player name and school, as you type. */
  q?: string;
  /** A manager id, or '' for all. Matches turns he took as well as players he won. */
  gm?: string;
  /** A `Pick['status']`, or '' for all. */
  status?: string;
}

/**
 * Every turn, newest season first, as ONE flat array with the season bands interleaved.
 *
 * Flat is the point. The old screen was a set of collapsible accordions, one per season, and the
 * cost of that was that you could not scroll from 2025 to 2013 — you had to know which years to
 * open first. A single array is also what makes the list virtualisable and the bands stickable:
 * both of those want a sequence with known offsets, not a tree.
 */
export function farmItems(filters: Filters = {}, turns: readonly Turn[] = TURNS): Item[] {
  const needle = (filters.q ?? '').trim().toLowerCase();
  const gm = (filters.gm ?? '').toUpperCase();
  const status = filters.status ?? '';

  const kept = turns.filter((t) => {
    // A GM filter must catch the turns he TOOK as well as the players he won, or filtering to one
    // manager silently hides every time he passed — which on this screen is half his history.
    if (gm && t.wonBy.toUpperCase() !== gm && t.nominator.toUpperCase() !== gm) return false;
    if (status && t.status !== status) return false;
    if (needle) {
      const hay = `${t.player} ${t.school ?? ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const seasons = [...new Set(kept.map((t) => t.season))].sort((a, b) => b - a);
  const out: Item[] = [];
  for (const season of seasons) {
    const rows = kept
      .filter((t) => t.season === season)
      .sort((a, b) => a.orderKey - b.orderKey);
    out.push({ kind: 'band', key: `band-${season}`, band: bandOf(season, rows, PICKS) });
    for (const turn of rows) out.push({ kind: 'turn', key: turn.key, turn });
  }
  return out;
}

/* -- Active Farm Rosters --------------------------------------------------------------------- */

export interface RosterRow {
  gm: string;
  held: Turn[];
}

/**
 * Every current manager, whether or not he holds anybody, plus any former manager who still does.
 *
 * A missing row reads as "not in the league"; an empty one reads as "three slots free", and on a
 * screen about a cap those are different facts.
 */
export function farmRosters(managers: readonly string[]): RosterRow[] {
  const held = new Map<string, Turn[]>();
  for (const t of TURNS) {
    if (t.status !== 'college') continue;
    const owner = t.wonBy.toUpperCase();
    if (!held.has(owner)) held.set(owner, []);
    held.get(owner)!.push(t);
  }
  const owners = [...new Set([...managers.map((m) => m.toUpperCase()), ...held.keys()])].sort();
  return owners.map((gm) => ({
    gm: title(gm),
    held: (held.get(gm) ?? []).sort((a, b) => b.season - a.season || (b.bid ?? 0) - (a.bid ?? 0)),
  }));
}

/** "9 of 30 slots" — the card's own header line. */
export function slotsUsed(rosters: readonly RosterRow[]): { used: number; total: number } {
  return {
    used: rosters.reduce((n, r) => n + r.held.length, 0),
    total: rosters.length * FARM_CAP,
  };
}
